import { atom } from "jotai";

export type ErrorReportSeverity = 'blocking' | 'annoying' | 'minor';

export type ErrorReportStatus = 'new' | 'triaged' | 'in_progress' | 'resolved' | 'wont_fix';

export const ERROR_REPORT_SEVERITIES: ErrorReportSeverity[] = ['blocking', 'annoying', 'minor'];

export const ERROR_REPORT_STATUSES: ErrorReportStatus[] = ['new', 'triaged', 'in_progress', 'resolved', 'wont_fix'];

// The Active section only ever holds these, so its filter dropdown offers only
// these — picking "Resolved" there would always come back empty.
export const ERROR_REPORT_ACTIVE_STATUSES: ErrorReportStatus[] = ['new', 'triaged', 'in_progress'];

// Which section of the admin page a report belongs to. `handled` = pushed to
// EXO Flow or closed; `deleted` = soft deleted and restorable.
export type ErrorReportBucket = 'active' | 'handled' | 'deleted';

// Rows-per-page choices offered on both section pagers.
export const ERROR_REPORT_PAGE_SIZE_OPTIONS = [10, 25, 50];

export const ERROR_REPORT_SEVERITY_LABELS: { [key in ErrorReportSeverity]: string } = {
    blocking: "Blocking — I can't work",
    annoying: 'Annoying — I can work around it',
    minor: 'Minor — cosmetic or small',
};

export const ERROR_REPORT_STATUS_LABELS: { [key in ErrorReportStatus]: string } = {
    new: 'New',
    triaged: 'Triaged',
    in_progress: 'In Progress',
    resolved: 'Resolved',
    wont_fix: "Won't Fix",
};

// Tailwind badge classes per status, used by both the admin table and the
// reporter's "my reports" list.
export const ERROR_REPORT_STATUS_BADGES: { [key in ErrorReportStatus]: string } = {
    new: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    triaged: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
    in_progress: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
    resolved: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200',
    wont_fix: 'bg-surface-strong text-stone-700 dark:bg-stone-700 dark:text-stone-200',
};

export const ERROR_REPORT_SEVERITY_BADGES: { [key in ErrorReportSeverity]: string } = {
    blocking: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    annoying: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
    minor: 'bg-surface-strong text-stone-700 dark:bg-stone-700 dark:text-stone-200',
};

export interface ErrorReportContext {
    page_url?: string | null;
    user_agent?: string | null;
    browser?: string | null;
    os?: string | null;
    viewport_width?: number | null;
    viewport_height?: number | null;
    app_version?: string | null;
}

export interface SubmitErrorReportPayload {
    description: string;
    severity: ErrorReportSeverity;
    images: { data: string; name?: string | null }[];
    context: ErrorReportContext;
}

export interface ErrorReportReporter {
    id: number;
    first: string | null;
    last: string | null;
    email: string | null;
}

export interface ErrorReportImage {
    id: number;
    error_report_id: number;
    storage_path: string;
    content_type: string;
    size_bytes: number;
    sort_order: number;
    created_at: string;
    signed_url: string | null;
}

export interface ErrorReportNote {
    id: number;
    error_report_id: number;
    author_user_id: number;
    note: string;
    created_at: string;
    author?: ErrorReportReporter | null;
}

export interface ErrorReport {
    id: number;
    reported_by_user_id: number;
    brand_id: number | null;
    description: string;
    severity: ErrorReportSeverity;
    status: ErrorReportStatus;
    page_url: string | null;
    user_agent: string | null;
    browser: string | null;
    os: string | null;
    viewport_width: number | null;
    viewport_height: number | null;
    app_version: string | null;
    reporter_permission: string | null;
    exo_flow_task_id: string | null;
    exo_flow_task_url: string | null;
    exo_flow_pushed_at: string | null;
    exo_flow_pushed_by: number | null;
    resolved_at: string | null;
    resolved_by: number | null;
    reporter_notified_at: string | null;
    // Soft delete. Set means the report is hidden from both sections but can be
    // restored; the detail endpoint still returns it so a deep link can explain.
    deleted_at: string | null;
    deleted_by: number | null;
    created_at: string;
    updated_at: string;
    reporter?: ErrorReportReporter | null;
}

export interface ErrorReportListItem extends ErrorReport {
    image_count: number;
    note_count: number;
    thumbnail_url: string | null;
}

export type ErrorReportHistoryAction =
    | 'created'
    | 'status_changed'
    | 'note_added'
    | 'screenshots_added'
    | 'pushed_to_exo_flow'
    | 'reporter_notified'
    | 'closed'
    | 'deleted'
    | 'restored';

// Column labels for the History tab. Unknown actions fall back to the raw value,
// so a newly added action type still renders rather than showing blank.
export const ERROR_REPORT_HISTORY_LABELS: { [key in ErrorReportHistoryAction]: string } = {
    created: 'Created',
    status_changed: 'Status Changed',
    note_added: 'Note Added',
    screenshots_added: 'Screenshots Added',
    pushed_to_exo_flow: 'Pushed to EXO Flow',
    reporter_notified: 'Reporter Notified',
    closed: 'Closed',
    deleted: 'Deleted',
    restored: 'Restored',
};

export const ERROR_REPORT_HISTORY_BADGES: { [key in ErrorReportHistoryAction]: string } = {
    created: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    status_changed: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
    note_added: 'bg-surface-strong text-stone-700 dark:bg-stone-700 dark:text-stone-200',
    screenshots_added: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200',
    pushed_to_exo_flow: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200',
    reporter_notified: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
    closed: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200',
    deleted: 'bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-200',
    restored: 'bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-200',
};

export interface ErrorReportHistoryEntry {
    id: number;
    error_report_id: number;
    action: ErrorReportHistoryAction;
    description: string;
    actor_user_id: number | null;
    created_at: string;
    actor?: ErrorReportReporter | null;
}

export interface ErrorReportDetail extends ErrorReport {
    images: ErrorReportImage[];
    notes: ErrorReportNote[];
    history: ErrorReportHistoryEntry[];
}

export interface ErrorReportListResponse {
    reports: ErrorReportListItem[];
    total: number;
    page: number;
    limit: number;
}

export interface ErrorReportFilters {
    // Section being loaded. Omitted keeps the legacy unsectioned list.
    bucket?: ErrorReportBucket;
    status?: string;
    severity?: string;
    // Free text matched server-side against the brand's id, name or Shopify name.
    brand?: string;
    // Free text matched server-side against the reporter's user id, name or email.
    reported_by?: string;
}

// Closing a report without pushing it anywhere. The note is optional; it is
// stored as an internal note so the reason lives on the ticket, not just in the
// audit trail.
export interface CloseErrorReportPayload {
    resolution: 'resolved' | 'wont_fix';
    note?: string;
    notify_reporter?: boolean;
}

export interface ExoFlowBoard {
    id: string;
    name: string;
}

export interface ExoFlowOptions {
    boards: ExoFlowBoard[];
    task_types: string[];
    team_statuses: string[];
    priorities: string[];
    configured: boolean;
}

export interface PushToExoFlowPayload {
    board_id?: string;
    task_type: string;
    team_status: string;
    priority: string;
    // Triager edits made in the push dialog. Blank falls back to the generated
    // title/body the preview endpoint handed over.
    title?: string;
    description?: string;
    ticket_note?: string;
    save_note_internally?: boolean;
    images?: { data: string; name?: string | null }[];
}

// Server-generated defaults for the push dialog, so the snapshot the admin edits
// is exactly what the server would otherwise have sent.
export interface ExoFlowPushPreview {
    title: string;
    description: string;
    screenshot_footer: string;
    image_count: number;
    default_priority: string;
    already_pushed: boolean;
}

// #region atoms
// Slideover visibility is global so the trigger can live in the app shell.
export const errorReportSlideoverAtom = atom<boolean>(false);

export const myErrorReportsAtom = atom<ErrorReport[]>([]);

// The admin page renders two independently paginated sections, so each one owns
// its rows, total and loading flag. A single shared set would make expanding
// Handled clobber whatever Active was showing.
export const adminActiveReportsAtom = atom<ErrorReportListItem[]>([]);
export const adminActiveReportsTotalAtom = atom<number>(0);
export const adminActiveReportsLoadingAtom = atom<boolean>(false);

// Also backs the "show deleted" view — it swaps the same table's data source.
export const adminHandledReportsAtom = atom<ErrorReportListItem[]>([]);
export const adminHandledReportsTotalAtom = atom<number>(0);
export const adminHandledReportsLoadingAtom = atom<boolean>(false);

export const adminErrorReportDetailAtom = atom<ErrorReportDetail | null>(null);

// Ticket / History tab selection inside the report detail slideover.
export const errorReportDetailTabAtom = atom<number>(0);
